import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  Paper,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Assessment as RiskIcon,
  Chat as ChatIcon,
  CheckCircleOutline,
  ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import analyticsService from '../../services/analytics';

const WELCOME_KEY = 'discovery_quiz_welcome';
const CHECK_INTERVAL_MS = 100; // Poll briefly after login to catch async localStorage set
const MAX_CHECKS = 10; // Stop polling after 1 second

interface WelcomeData {
  route: string;
  persona: string;
  goal: string;
  risk?: string;
  horizon?: string;
  needs?: string[];
}

// Label mappings for profile preview
const PERSONA_LABELS: Record<string, string> = {
  builder: 'Portfolio Builder',
  manager: 'Active Manager',
  researcher: 'Stock Researcher',
};

const GOAL_LABELS: Record<string, string> = {
  growth: 'Growth',
  income: 'Income',
  preservation: 'Preservation',
  balanced: 'Balanced',
};

const RISK_LABELS: Record<string, string> = {
  conservative: 'Conservative',
  moderate: 'Moderate',
  aggressive: 'Aggressive',
};

const HORIZON_LABELS: Record<string, string> = {
  short: 'Less than 1 year',
  medium: '1 - 5 years',
  long: '5+ years',
};

interface ToolInfo {
  title: string;
  icon: React.ElementType;
  description: string;
  features: string[];
  benefits: Record<string, string>;
}

const TOOL_INFO: Record<string, ToolInfo> = {
  '/stock-research': {
    title: 'Stock Research',
    icon: SearchIcon,
    description:
      'Multi-factor analysis on any stock — fundamentals, valuation, dividend quality, and recent news, with every input auditable.',
    features: [
      'Stock scoring — find undervalued opportunities others miss',
      'Dividend quality analysis — see which dividends are safe',
      'AI-generated research summaries',
    ],
    benefits: {
      builder:
        'Start by exploring real stocks to build conviction before you invest.',
      manager:
        'Research individual positions in your portfolio for deeper insights.',
      researcher:
        'Your primary workbench for fast, reliable analysis before making moves.',
    },
  },
  '/portfolio/risk-assessment': {
    title: 'Portfolio Risk Assessment',
    icon: RiskIcon,
    description:
      'Upload your holdings and get comprehensive risk analysis including VaR, stress testing, and diversification scoring.',
    features: [
      'Value-at-Risk (VaR) and Conditional VaR calculations',
      'Stress testing against historical market scenarios',
      'Factor exposure and diversification analysis',
    ],
    benefits: {
      builder:
        'Understand the risk profile of your investments as you build.',
      manager:
        'Identify hidden vulnerabilities and optimize your risk-return profile.',
      researcher:
        'Analyze risk factors to inform your research and recommendations.',
    },
  },
  '/chat': {
    title: 'AI Assistant',
    icon: ChatIcon,
    description:
      'Ask questions, generate research reports, explore market trends, and get personalized investment guidance.',
    features: [
      'On-demand research reports with charts and data tables',
      'Portfolio analysis and stock comparison',
      'Market trend and sector analysis',
    ],
    benefits: {
      builder:
        'Get step-by-step guidance on building your first portfolio.',
      manager:
        'Generate on-demand reports and analysis on your portfolio.',
      researcher:
        'Create detailed research reports with charts, tables, and data.',
    },
  },
};

const PostLoginWelcome: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [welcomeData, setWelcomeData] = useState<WelcomeData | null>(null);
  const checkCountRef = useRef(0);

  useEffect(() => {
    if (!user) {
      checkCountRef.current = 0;
      return;
    }

    const checkForWelcomeData = () => {
      const raw = localStorage.getItem(WELCOME_KEY);
      if (!raw) return false;

      try {
        const data: WelcomeData = JSON.parse(raw);
        if (data.route && data.persona) {
          setWelcomeData(data);
          analyticsService.trackWelcomeShown(data.route, data.persona);
          return true;
        } else {
          localStorage.removeItem(WELCOME_KEY);
        }
      } catch {
        localStorage.removeItem(WELCOME_KEY);
      }
      return false;
    };

    // Try immediately first
    if (checkForWelcomeData()) return;

    // Poll briefly to catch async localStorage set from useDiscoveryQuizSaver
    // This handles the race condition where the hook sets localStorage after this effect runs
    const intervalId = setInterval(() => {
      checkCountRef.current++;
      if (checkForWelcomeData() || checkCountRef.current >= MAX_CHECKS) {
        clearInterval(intervalId);
      }
    }, CHECK_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [user]);

  const handleGo = useCallback(() => {
    if (!welcomeData) return;

    analyticsService.trackWelcomeCtaClicked('go_to_tool', welcomeData.route);

    localStorage.removeItem(WELCOME_KEY);
    setWelcomeData(null);
    navigate(welcomeData.route);
  }, [welcomeData, navigate]);

  const handleDismiss = useCallback(() => {
    if (!welcomeData) return;

    analyticsService.trackWelcomeCtaClicked('dismiss', welcomeData.route);

    localStorage.removeItem(WELCOME_KEY);
    setWelcomeData(null);

    // Always navigate to the recommended tool - users have set up preferences
    // and are ready to try the platform, don't leave them on landing page
    navigate(welcomeData.route);
  }, [welcomeData, navigate]);

  if (!welcomeData) return null;

  const tool = TOOL_INFO[welcomeData.route];
  if (!tool) {
    localStorage.removeItem(WELCOME_KEY);
    return null;
  }

  const ToolIcon = tool.icon;
  const benefit =
    tool.benefits[welcomeData.persona] ?? tool.description;

  return (
    <Dialog
      open
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ px: { xs: 3, sm: 4 }, py: 4 }}>
        {/* Success confirmation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CheckCircleOutline sx={{ fontSize: 20, color: 'success.main' }} />
          <Typography
            variant="body2"
            sx={{ color: 'success.main', fontWeight: 500 }}
          >
            Your preferences have been saved
          </Typography>
        </Box>

        {/* Profile preview */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.default',
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontSize: 10,
              letterSpacing: 1.2,
              mb: 1.5,
              display: 'block',
            }}
          >
            Your Investment Profile
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                Profile
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {PERSONA_LABELS[welcomeData.persona] ?? 'Portfolio Builder'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                Goal
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {GOAL_LABELS[welcomeData.goal] ?? 'Balanced'}
              </Typography>
            </Box>
            {welcomeData.risk && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                  Risk
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {RISK_LABELS[welcomeData.risk] ?? 'Moderate'}
                </Typography>
              </Box>
            )}
            {welcomeData.horizon && (
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                  Timeline
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {HORIZON_LABELS[welcomeData.horizon] ?? '1 - 5 years'}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Tool intro */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.text.primary, 0.08),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ToolIcon sx={{ fontSize: 22, color: 'text.primary' }} />
          </Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: 'text.primary' }}
          >
            {tool.title}
          </Typography>
        </Box>

        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', mb: 2, lineHeight: 1.6 }}
        >
          {tool.description}
        </Typography>

        {/* Personalized benefit */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.default',
          }}
        >
          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
              fontSize: 10,
              letterSpacing: 1.2,
              mb: 1,
              display: 'block',
            }}
          >
            Why this is recommended for you
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.primary', fontWeight: 500 }}
          >
            {benefit}
          </Typography>
        </Paper>

        {/* Key features */}
        <Typography
          variant="overline"
          sx={{
            color: 'text.secondary',
            fontSize: 10,
            letterSpacing: 1.2,
            mb: 1,
            display: 'block',
          }}
        >
          What you can do
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 3 }}>
          {tool.features.map((feature) => (
            <Typography
              component="li"
              key={feature}
              variant="body2"
              sx={{ color: 'text.secondary', mb: 0.5, lineHeight: 1.5 }}
            >
              {feature}
            </Typography>
          ))}
        </Box>

        {/* CTAs */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleGo}
          endIcon={<ArrowForward />}
          sx={{
            py: 1.25,
            bgcolor: 'text.primary',
            color: 'background.paper',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { opacity: 0.85 },
          }}
        >
          Go to {tool.title}
        </Button>

        <Button
          fullWidth
          onClick={handleDismiss}
          sx={{
            mt: 1,
            py: 1,
            color: 'text.secondary',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              bgcolor: alpha(theme.palette.text.primary, 0.04),
            },
          }}
        >
          Skip intro
        </Button>
      </Box>
    </Dialog>
  );
};

export default PostLoginWelcome;
